import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, Table, theme } from 'antd';
import { LlmPromptDto, LlmPromptVersionDto } from '@wellverse/shared-ts';
import { Field } from '../../../components/Field';

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const LlmPromptShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<LlmPromptDto>({ resource: 'llm-prompts' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const record = item as LlmPromptDto & {
    versions?: LlmPromptVersionDto[];
    createdAt?: string;
    updatedAt?: string;
  };

  const versions = record.versions || [];

  return (
    <Show resource="llm-prompts">
      <div style={{ padding: '8px 0' }}>
        <Field label="Name">{record.name}</Field>
        <Field label="Slug">{record.slug}</Field>
        <Field label="Description">{record.description || '—'}</Field>
        {record.variables && record.variables.length > 0 && (
          <Field label="Variables">
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {record.variables.map((v) => (
                <Tag key={v} style={{ borderRadius: 9999, padding: '2px 8px' }}>
                  {v}
                </Tag>
              ))}
            </div>
          </Field>
        )}
        <Field label="Active">
          <Tag style={tagStyle(record.isActive ? '#3ecf8e' : '#898989')}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
        </Field>
        {record.activeVersion && (
          <Field label="Active Version">
            <Tag style={tagStyle('#1890ff')}>v{record.activeVersion.version}</Tag>
          </Field>
        )}
        {record.createdAt && (
          <Field label="Created At">{new Date(record.createdAt).toLocaleString()}</Field>
        )}
        {record.updatedAt && (
          <Field label="Updated At">{new Date(record.updatedAt).toLocaleString()}</Field>
        )}
      </div>

      {versions.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 style={{ marginBottom: 12 }}>Versions</h4>
          <Table
            dataSource={versions}
            rowKey="id"
            pagination={false}
            size="small"
          >
            <Table.Column
              dataIndex="version"
              title="Version"
              width={100}
              render={(version: number) => (
                <Tag style={tagStyle('#1890ff')}>v{version}</Tag>
              )}
            />
            <Table.Column
              dataIndex="changeNote"
              title="Change Note"
              render={(note: string) => note || '—'}
            />
            <Table.Column
              dataIndex="createdAt"
              title="Date"
              render={(date: string) => new Date(date).toLocaleString()}
            />
            <Table.Column
              dataIndex="isActive"
              title="Active"
              width={100}
              render={(isActive: boolean) => (
                <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
                  {isActive ? 'Active' : '—'}
                </Tag>
              )}
            />
          </Table>
        </div>
      )}
    </Show>
  );
};
